export class PolygonSeedsFillAnimation {
  constructor(options) {
    const { seeds, speed, scene, polygon } = options;
    this.ctx = scene.ctx;
    this.canvas = scene.canvas;
    this.scene = scene;
    this.seeds = seeds;
    this.speed = speed;

    this.polygon = polygon;

    this.isFinishing = false;

    this.totalProcessed = 0;
    this.totalRemaining = 0;

    this.renderTask = {};

    this.filled = [];

    this.colorRecord = {};

    this.initFilledArray();
  }

  log(...args) {
    console.log(...args);
  }

  initFilledArray() {
    this.filled = new Array(this.canvas.width);
    for (let x = 0; x < this.canvas.width; x++) {
      this.filled[x] = new Array(this.canvas.height).fill(false);
    }
  }

  drawPolygon() {
    this.renderTask['base-polygon'] = () => {
      this.ctx.beginPath();
      this.ctx.moveTo(this.polygon[0].x, this.polygon[0].y);
      for (let i = 1; i < this.polygon.length; i++) {
        this.ctx.lineTo(this.polygon[i].x, this.polygon[i].y);
      }
      this.ctx.closePath();
      this.ctx.strokeStyle = 'red';
      this.ctx.lineWidth = 2;
      this.ctx.stroke();
    };
  }

  pointInPolygon(px, py) {
    let inside = false;
    for (let i = 0, j = this.polygon.length - 1; i < this.polygon.length; j = i++) {
      const xi = this.polygon[i].x,
        yi = this.polygon[i].y;
      const xj = this.polygon[j].x,
        yj = this.polygon[j].y;

      const onEdge =
        (py === yi && py === yj && px >= Math.min(xi, xj) && px <= Math.max(xi, xj)) ||
        (px === xi && px === xj && py >= Math.min(yi, yj) && py <= Math.max(yi, yj));
      if (onEdge) return true;

      const intersect = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi + 1e-9) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }

  // 像素点转扫描线区间
  pixelsToScanlines(pixels) {
    const lines = {};
    pixels.forEach(([x, y]) => {
      if (!lines[y]) lines[y] = [];
      lines[y].push(x);
    });

    Object.keys(lines).forEach(y => {
      lines[y].sort((a, b) => a - b);
      const merged = [];
      let start = lines[y][0];
      let end = start;
      for (let i = 1; i < lines[y].length; i++) {
        if (lines[y][i] === end + 1) {
          end = lines[y][i];
        } else {
          merged.push([start, end]);
          start = lines[y][i];
          end = start;
        }
      }
      merged.push([start, end]);
      lines[y] = merged;
    });
    return lines;
  }

  // 扫描线转多边形
  scanlinesToPolygons(lines) {
    const polygons = [];
    Object.keys(lines).forEach(y => {
      const ints = lines[y];
      ints.forEach(([sx, ex]) => {
        polygons.push([
          { x: sx, y: parseInt(y) },
          { x: ex + 1, y: parseInt(y) },
          { x: ex + 1, y: parseInt(y) + 1 },
          { x: sx, y: parseInt(y) + 1 }
        ]);
      });
    });
    return polygons;
  }

  // 批量绘制多边形
  drawPolygons(polygons, color) {
    this.ctx.fillStyle = color;

    polygons.forEach(poly => {
      this.ctx.beginPath();
      this.ctx.moveTo(poly[0].x, poly[0].y);
      for (let i = 1; i < poly.length; i++) {
        this.ctx.lineTo(poly[i].x, poly[i].y);
      }
      this.ctx.closePath();
      this.ctx.fill();
    });
  }

  multiPointFloodFill() {
    const queues = this.seeds.map(seed => ({
      queue: [[seed.x, seed.y]],
      color: seed.color,
      processed: 0 // 每个队列独立计数
    }));

    const dirs = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, 1],
      [1, -1],
      [-1, 1],
      [-1, -1]
    ];

    const animate = () => {
      const batchByColor = {};

      if (this.isFinishing) {
        this.drawPolygons([this.polygon], queues[0].color);

        return;
      }

      queues.forEach(q => {
        q.processed = 0;
        // 每个队列都能处理this.speed个像素，而不是平均分配
        while (q.queue.length > 0 && q.processed < this.speed) {
          const [x, y] = q.queue.shift();
          const ix = Math.floor(x);
          const iy = Math.floor(y);

          if (ix < 0 || iy < 0 || ix >= this.canvas.width || iy >= this.canvas.height) continue;
          if (this.filled[ix][iy]) continue;
          if (!this.pointInPolygon(ix, iy)) continue;

          this.filled[ix][iy] = true;

          const key = q.color;
          if (!batchByColor[key]) batchByColor[key] = [];
          batchByColor[key].push([ix, iy]);

          for (const [dx, dy] of dirs) {
            q.queue.push([ix + dx, iy + dy]);
          }

          q.processed++;
        }

        this.totalRemaining += q.queue.length;
      });

      const len = queues.filter(e => e.queue.length > 0);

      if (len.length === 0 && !this.isFinishing) {
        this.isFinishing = true;
        this.finishedCbs.forEach(e => {
          e?.();
        });
      }

      // 批量绘制
      Object.keys(batchByColor).forEach(color => {
        const lines = this.pixelsToScanlines(batchByColor[color]);
        const polys = this.scanlinesToPolygons(lines);

        if (polys.length > 0) this.scene.isNeedUpdate = true;

        if (!this.colorRecord[color]) this.colorRecord[color] = [];

        this.colorRecord[color].push(...polys);
      });

      Object.entries(this.colorRecord).forEach(([color, polys]) => {
        this.drawPolygons(polys, color);
      });
    };

    this.renderTask['render'] = () => animate();
  }

  addSeedPoint(x, y, color) {
    this.seeds.push({ x, y, color });

    this.scene.isNeedUpdate = true;

    this.multiPointFloodFill();
  }

  onFinishing(cb) {
    if (!this.finishedCbs) this.finishedCbs = [];

    this.finishedCbs.push(cb);
  }

  reset() {
    this.seeds = [];
    this.initFilledArray();
    this.drawPolygon();
  }

  setSpeed(val) {
    this.speed = val;
    this.isFinishing = false;
  }

  getSpeed() {
    return this.speed;
  }

  update() {
    this.renderTask['base-polygon']?.();
    this.renderTask['render']?.();
  }
}
